import { EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import type { ActivityEntry, Order } from './domain'
import { ACTIVITY_ACTOR_KIND_LABEL, ACTIVITY_KIND_LABEL, formatDateTime } from './domain'

export interface ActivityLogPanelProps {
  readonly order: Pick<Order, 'activityLog'>
  readonly open: boolean
  readonly onClose: () => void
}

/**
 * Order activity log (UID-121 S7). Replaces the previously disabled "View
 * activity log" affordance in `OrderSummaryCard` — `order.activityLog` is now
 * real, backend-sourced data (a chronological, order-scoped projection of the
 * generic cross-aggregate `ActivityEntry` feed, ADR-0046). Rendered as a
 * Modal (components.md "Modal" — the shared modal/drawer shell already used
 * elsewhere in this app) rather than a navigation, matching the prototype's
 * lightweight-overlay intent for `orderActivity` ("Opening activity log").
 *
 * `activityLog` is documented as already server-ordered chronologically
 * (schema.gen.ts), so this renders the array as given rather than re-sorting
 * client-side.
 */
export function ActivityLogPanel({ order, open, onClose }: ActivityLogPanelProps) {
  const entries = order.activityLog ?? []

  return (
    <Modal open={open} onClose={onClose} title="Activity log" maxWidth={560}>
      {entries.length === 0 ? (
        <EmptyState
          title="No activity yet"
          detail="Updates to this order will show up here as they happen."
        />
      ) : (
        <ol className="activity-log-list">
          {entries.map((entry) => (
            <ActivityLogEntryRow key={entry.id} entry={entry} />
          ))}
        </ol>
      )}
    </Modal>
  )
}

function ActivityLogEntryRow({ entry }: { readonly entry: ActivityEntry }) {
  return (
    <li className="activity-log-entry">
      <div className="activity-log-entry-header">
        <span className="activity-log-entry-kind">{ACTIVITY_KIND_LABEL[entry.kind]}</span>
        <time className="activity-log-entry-time caption-text" dateTime={entry.occurredAt}>
          {formatDateTime(entry.occurredAt)}
        </time>
      </div>
      <p className="activity-log-entry-message">{entry.message}</p>
      {entry.actor && (
        <p className="caption-text activity-log-entry-actor">
          {entry.actor}
          {entry.actorKind ? ` · ${ACTIVITY_ACTOR_KIND_LABEL[entry.actorKind]}` : ''}
        </p>
      )}
    </li>
  )
}
